// Minimal inline icon set (Lucide-style, 24-grid, 1.75 stroke, currentColor). No dependency.

import type { SVGProps } from "react";

const P = (d: string | string[]) => (Array.isArray(d) ? d : [d]);

const PATHS: Record<string, string[]> = {
  overview: P(["M4 13h7V4H4zM13 20h7v-9h-7zM13 4v5h7V4zM4 20h7v-5H4z"]),
  box: P([
    "M21 8 12 3 3 8v8l9 5 9-5z",
    "M3 8l9 5 9-5",
    "M12 13v8",
  ]),
  calendarDay: P(["M3 8h18", "M8 3v3M16 3v3", "M5 5h14v16H5z", "M8 13h3v3H8z"]),
  calendar: P(["M3 8h18", "M8 3v3M16 3v3", "M5 5h14v16H5z"]),
  calendarRange: P(["M3 8h18", "M8 3v3M16 3v3", "M5 5h14v16H5z", "M8 13h8", "M8 17h5"]),
  reports: P(["M8 3h9l4 4v14H8z", "M7 7H3v14h11", "M12 12h5M12 16h5"]),
  scale: P(["M12 3v18", "M7 21h10", "M6 7h12", "M6 7 3 13h6zM18 7l3 6h-6z"]),
  alert: P(["M12 4 2 20h20z", "M12 10v5", "M12 18h.01"]),
  settings: P([
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
    "M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2l-.4-2.6H9.9l-.4 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2l.4 2.6h4.2l.4-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z",
  ]),
  star: P(["M12 3.5 14.6 9l6 .8-4.3 4.2 1 6L12 17.2 6.7 20l1-6L3.4 9.8l6-.8z"]),
  refresh: P(["M21 12a9 9 0 1 1-3-6.7", "M21 4v5h-5"]),
  download: P(["M12 4v11", "M8 11l4 4 4-4", "M5 19h14"]),
  x: P(["M6 6l12 12M18 6 6 18"]),
  check: P(["M5 12l5 5 9-11"]),
  clock: P(["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M12 7v5l3 2"]),
  chevron: P(["M9 6l6 6-6 6"]),
  search: P(["M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z", "M21 21l-4.3-4.3"]),
  user: P(["M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z", "M4 21c1.4-4 4.5-6 8-6s6.6 2 8 6"]),
  lock: P(["M6 11V8a6 6 0 0 1 12 0v3", "M5 11h14v10H5z"]),
  eye: P(["M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z", "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"]),
  eyeOff: P([
    "M3 3l18 18",
    "M10.6 5.2A10.6 10.6 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.4 4.4",
    "M6.5 6.6C4 8.3 2 12 2 12s3.6 7 10 7a9.9 9.9 0 0 0 3.4-.6",
    "M9.5 9.5a3 3 0 0 0 4.2 4.2",
  ]),
  arrowRight: P(["M5 12h14", "M13 6l6 6-6 6"]),
  truck: P(["M3 7h10v9H3z", "M13 10h4l4 3v3h-8z", "M6.5 19.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z", "M17.5 19.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"]),
  tag: P(["M3 12V4h8l10 10-8 8z", "M7.5 8.5h.01"]),
  shield: P(["M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6z", "M9 12l2 2 4-4"]),
  info: P(["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M12 11v6", "M12 7.5h.01"]),
  menu: P(["M4 7h16", "M4 12h16", "M4 17h16"]),
  sun: P([
    "M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    "M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  ]),
  moon: P(["M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"]),
  monitor: P(["M3 4h18v12H3z", "M8 20h8", "M12 16v4"]),
  logout: P(["M9 21H5V3h4", "M16 17l5-5-5-5", "M21 12H9"]),
  // toolbar set (2026-09-28: replaces the text glyphs ⧉ ↺ ☑ ⇄ ⊞ ☰ ＋ 📌 ⇥ ⚙)
  copy: P(["M9 9h11v11H9z", "M5 15H4V4h11v1"]),
  reset: P(["M3 12a9 9 0 1 0 3-6.7", "M3 4v5h5"]),
  checkSquare: P(["M4 4h16v16H4z", "M8 12l3 3 5-6"]),
  compare: P(["M7 4 3 8l4 4", "M3 8h14", "M17 20l4-4-4-4", "M21 16H7"]),
  columns: P(["M3 4h18v16H3z", "M9 4v16", "M15 4v16"]),
  layers: P(["M12 3 2 8l10 5 10-5z", "M2 16l10 5 10-5", "M2 12l10 5 10-5"]),
  list: P(["M8 6h13", "M8 12h13", "M8 18h13", "M3 6h.01", "M3 12h.01", "M3 18h.01"]),
  plus: P(["M12 5v14", "M5 12h14"]),
  pin: P(["M9 3h6", "M10 3v6l-4 5h12l-4-5V3", "M12 14v7"]),
  pinRight: P(["M3 4h18v16H3z", "M15 4v16"]),
  save: P(["M5 3h11l3 3v15H5z", "M8 3v5h7", "M8 21v-7h8v7"]),
};

export function Icon({
  name,
  size = 18,
  ...rest
}: { name: keyof typeof PATHS | string; size?: number } & SVGProps<SVGSVGElement>) {
  const paths = PATHS[name] ?? PATHS.box;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}
